// API connections for explicit build and planning-stage selection. Native subscription login
// stays in the official local agent and is configured separately through the Serve guide.
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { CheckCircle2, KeyRound, Loader2, Trash2, XCircle } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '#/components/ui/badge'
import { Button } from '#/components/ui/button'
import { Input } from '#/components/ui/input'
import { Label } from '#/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '#/components/ui/select'
import { ApiError } from '#/lib/api'
import { useAiConnections, useCreateAiConnection, useDeleteAiConnection, useTestAiConnection } from '#/lib/api-byoa'
import { AI_PROVIDERS, PROVIDER_CONSOLE_URL, PROVIDER_MODELS, type AiProvider } from '#/lib/byoa'
import { looksLikeSubscriptionToken } from '#/lib/byoa-guard'
import { useBuilderMode } from '#/lib/simple-mode'
import { fmtDate } from '#/lib/format'
import { m } from '#/paraglide/messages'

const providerLabel = (p: AiProvider) => (p === 'anthropic' ? m.byoa_provider_anthropic() : p === 'openai' ? m.byoa_provider_openai() : p === 'openrouter' ? m.byoa_provider_openrouter() : m.byoa_provider_custom())

export function AiConnectionsCard({ enabled = true }: { enabled?: boolean }) {
  const { simple } = useBuilderMode()
  const { data } = useAiConnections(enabled)
  const create = useCreateAiConnection()
  const remove = useDeleteAiConnection()
  const test = useTestAiConnection()

  const [provider, setProvider] = useState<AiProvider>('anthropic')
  const [apiKey, setApiKey] = useState('')
  const [model, setModel] = useState('')
  const [baseUrl, setBaseUrl] = useState('')
  const [label, setLabel] = useState('')
  const [tested, setTested] = useState<Record<string, { ok: boolean; text: string }>>({})
  const [rejected, setRejected] = useState(false)

  const connections = data?.connections ?? []
  const models = PROVIDER_MODELS[provider]
  const consoleUrl = PROVIDER_CONSOLE_URL[provider]

  const pickProvider = (v: string) => {
    const p = (AI_PROVIDERS as readonly string[]).includes(v) ? (v as AiProvider) : 'custom'
    setProvider(p)
    setModel(PROVIDER_MODELS[p][0] ?? '')
  }

  const save = () => {
    create.mutate(
      { provider, apiKey: apiKey.trim(), model: model.trim(), baseUrl: baseUrl.trim() || null, label: label.trim() || undefined },
      {
        onSuccess: () => {
          setApiKey('')
          setLabel('')
          toast.success(m.byoa_saved())
          setRejected(false)
        },
        onError: (e) => {
          // Orkestrator E0/E3: a pasted subscription login is refused — say why and where the rules are
          setRejected(e instanceof ApiError && e.code === 'subscription_token_rejected')
          toast.error(e instanceof ApiError ? e.message : m.common_error_generic())
        },
      },
    )
  }

  const runTest = (id: string) => {
    test.mutate(id, {
      onSuccess: (r) => setTested((prev) => ({ ...prev, [id]: { ok: r.ok, text: r.ok ? m.byoa_test_ok({ ms: r.ms }) : m.byoa_test_fail({ detail: r.detail }) } })),
      onError: (e) => setTested((prev) => ({ ...prev, [id]: { ok: false, text: m.byoa_test_fail({ detail: e instanceof ApiError ? e.message : 'error' }) } })),
    })
  }

  // a pasted subscription login never leaves the browser: the server would refuse it anyway (E0 guard)
  const subscriptionPaste = looksLikeSubscriptionToken(apiKey)
  const canSave = apiKey.trim().length >= 8 && model.trim().length > 0 && (provider !== 'custom' || /^https?:\/\//i.test(baseUrl.trim())) && !subscriptionPaste

  return (
    <section className="space-y-4 rounded-2xl border bg-card p-5" data-testid="byoa-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg font-semibold">{simple ? m.byoa_title_simple() : m.byoa_title()}</h2>
          <p className="text-sm text-muted-foreground">{m.byoa_desc()}</p>
        </div>
        <KeyRound className="size-5 shrink-0 text-muted-foreground" />
      </div>

      {connections.length ? (
        <ul className="divide-y rounded-xl border">
          {connections.map((c) => {
            const t = tested[c.id]
            return (
              <li key={c.id} className="space-y-1 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{c.label || providerLabel(c.provider)}</span>
                  <Badge variant="secondary">{providerLabel(c.provider)}</Badge>
                  <Badge variant="outline">{m.byoa_stage_api_badge()}</Badge>
                  <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{c.model}</code>
                  <code className="font-mono text-xs text-muted-foreground">{c.keyHint}</code>
                  <span className="flex-1" />
                  <Button aria-busy={test.isPending} size="sm" variant="outline" className="h-7" onClick={() => runTest(c.id)} disabled={test.isPending}>
                    {test.isPending && test.variables === c.id ? <Loader2 className="size-3.5 animate-spin" /> : null}
                    {test.isPending && test.variables === c.id ? m.byoa_testing() : m.byoa_test()}
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="size-7"
                    aria-label={m.byoa_delete()}
                    onClick={() =>
                      remove.mutate(c.id, {
                        onSuccess: () => toast.success(m.byoa_deleted()),
                        onError: (e) => toast.error(e instanceof ApiError ? e.message : m.common_error_generic()),
                      })
                    }
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  {t ? (
                    <>
                      {t.ok ? <CheckCircle2 className="size-3.5 text-status-done" /> : <XCircle className="size-3.5 text-status-blocked" />}
                      {t.text}
                    </>
                  ) : c.lastOkAt ? (
                    m.byoa_last_ok({ when: fmtDate(c.lastOkAt) })
                  ) : (
                    m.byoa_never_tested()
                  )}
                </p>
                <p className="text-xs text-muted-foreground">{m.byoa_stage_capabilities()}</p>
              </li>
            )
          })}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">{m.byoa_empty()}</p>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="byoa-provider">{m.byoa_field_provider()}</Label>
          <Select value={provider} onValueChange={pickProvider}>
            <SelectTrigger id="byoa-provider">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AI_PROVIDERS.map((p) => (
                <SelectItem key={p} value={p}>
                  {providerLabel(p)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="byoa-key">{m.byoa_field_key()}</Label>
          <Input id="byoa-key" type="password" autoComplete="off" value={apiKey} onChange={(e) => { setApiKey(e.target.value); setRejected(false) }} placeholder={m.byoa_key_placeholder()} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="byoa-model">{m.byoa_field_model()}</Label>
          <Input id="byoa-model" list="byoa-model-options" value={model} onChange={(e) => setModel(e.target.value)} placeholder={m.byoa_model_placeholder()} />
          <datalist id="byoa-model-options">
            {models.map((x) => (
              <option key={x} value={x} />
            ))}
          </datalist>
        </div>
        {provider === 'custom' ? (
          <div className="space-y-1.5">
            <Label htmlFor="byoa-base">{m.byoa_field_base_url()}</Label>
            <Input id="byoa-base" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder={m.byoa_base_url_placeholder()} />
          </div>
        ) : (
          <div className="space-y-1.5">
            <Label htmlFor="byoa-label">{m.byoa_field_label()}</Label>
            <Input id="byoa-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder={m.byoa_label_placeholder()} />
          </div>
        )}
      </div>

      <div className="space-y-1 text-xs text-muted-foreground">
        <p>{provider === 'custom' ? m.byoa_help_custom() : m.byoa_help()}</p>
        {consoleUrl ? (
          <p>
            <a className="underline underline-offset-2" href={consoleUrl} target="_blank" rel="noreferrer noopener">
              {m.byoa_get_key({ provider: providerLabel(provider) })}
            </a>
          </p>
        ) : null}
        <p className="font-medium text-foreground">{m.byoa_cost_warning()}</p>
        <p>{m.byoa_no_oauth()}</p>
        {/* Orkestrator E3 §4: what each vendor allows, dated and sourced */}
        <Link to="/kebijakan-penyedia" className="inline-block underline underline-offset-2" data-testid="byoa-policy-link">{m.pol_link_more()}</Link>
        <Link to="/serve" className="inline-block underline underline-offset-2">{m.byoa_local_setup()}</Link>
      </div>

      {rejected || subscriptionPaste ? (
        <p role="alert" className="rounded-lg border border-status-blocked/40 bg-status-blocked/5 p-2 text-xs" data-testid="byoa-subscription-rejected">
          {m.byoa_subscription_token_rejected()}{' '}
          <Link to="/kebijakan-penyedia" className="underline underline-offset-2">{m.pol_reject_why()}</Link>
        </p>
      ) : null}

      <Button aria-busy={create.isPending} size="sm" onClick={save} disabled={!canSave || create.isPending}>
        {create.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
        {create.isPending ? m.byoa_saving() : m.byoa_save()}
      </Button>
    </section>
  )
}
